import { useEffect, useRef, useState } from "react";
import { publicPost } from "../lib/api";

declare global {
  interface Window {
    turnstile?: {
      render: (selector: string | HTMLElement, opts: { sitekey: string; callback: (token: string) => void }) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export default function PublicTicketForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState<{ ticketNumber: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);

  const siteKey =
    import.meta.env.TURNSTILE_SITE_KEY ?? import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;

    const render = () => {
      if (!containerRef.current || widgetId.current) return;
      if (!window.turnstile) return;
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (t) => setToken(t),
      });
    };

    if (window.turnstile) {
      render();
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = render;
    document.head.appendChild(script);
    return () => { script.remove(); };
  }, [siteKey]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Bevestig de captcha om door te gaan.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicPost<{ ticketNumber: number }>("/api/tickets/public", {
        name, email, subject, description, cfTurnstileResponse: token,
      });
      setConfirmation(res);
    } catch (err) {
      setError((err as Error).message);
      if (window.turnstile && widgetId.current) window.turnstile.reset(widgetId.current);
      setToken("");
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="public-shell">
        <h2>Bedankt!</h2>
        <p>Je ticket <strong>#{confirmation.ticketNumber}</strong> is aangemaakt.</p>
        <p>Je ontvangt een bevestigingsmail. Antwoord gerust rechtstreeks op die mail om extra info mee te geven.</p>
      </div>
    );
  }

  return (
    <div className="public-shell">
      <h2>Een nieuw ticket aanmaken</h2>
      <p style={{ color: "var(--text-muted)" }}>
        Vul het formulier hieronder in. We nemen zo snel mogelijk contact met je op.
      </p>
      <form onSubmit={submit}>
        <div className="field">
          <label>Naam</label>
          <input required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label>E-mailadres</label>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div className="field">
          <label>Onderwerp</label>
          <input required value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="field">
          <label>Beschrijving</label>
          <textarea required value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="field" ref={containerRef} />
        {error && <div style={{ color: "var(--danger)", marginBottom: 12 }}>{error}</div>}
        <button className="primary" type="submit" disabled={submitting}>
          {submitting ? "Verzenden…" : "Ticket aanmaken"}
        </button>
      </form>
    </div>
  );
}
